import { useMemo } from 'react';

const UNASSIGNED_DRIVER_BASE = {
    __typename: 'Driver',
    name: 'Unassigned Driver',
    externalId: null,
    driverId: 0,
    id: 0,
    status: 1,
    contactInfo: '',
};

const SEARCHABLE_FIELDS = ['searchLabel', 'name', 'label', 'userName', 'externalId', 'driverId', 'id'];

const ensureArray = (collection) => (Array.isArray(collection) ? collection : []);

const createUnassignedDriver = (template) => ({
    ...template,
    ...UNASSIGNED_DRIVER_BASE,
    contactInfo: typeof template?.contactInfo === 'string'
        ? template.contactInfo
        : UNASSIGNED_DRIVER_BASE.contactInfo,
});

const getSortKey = (item) => {
    const label = item?.label || item?.name || item?.userName || '';
    return typeof label === 'string' ? label : String(label);
};

const sortAlpha = (arr) => structuredClone(arr).sort((a, b) =>
    getSortKey(a).localeCompare(getSortKey(b), undefined, { sensitivity: 'base', numeric: true })
);

const matchesSearch = (item, normalizedSearch) => {
    if (!item) {
        return false;
    }

    for (let index = 0; index < SEARCHABLE_FIELDS.length; index += 1) {
        const fieldName = SEARCHABLE_FIELDS[index];
        const value = item[fieldName];

        if (typeof value === 'string') {
            if (value.length > 0 && value.toLowerCase().includes(normalizedSearch)) {
                return true;
            }
        } else if (typeof value === 'number' || typeof value === 'bigint') {
            if (String(value).includes(normalizedSearch)) {
                return true;
            }
        }
    }

    const tags = item.tags;
    if (Array.isArray(tags)) {
        for (let tagIndex = 0; tagIndex < tags.length; tagIndex += 1) {
            const tag = tags[tagIndex];

            if (typeof tag === 'string') {
                if (tag.toLowerCase().includes(normalizedSearch)) {
                    return true;
                }
            } else if (tag && typeof tag.name === 'string') {
                if (tag.name.toLowerCase().includes(normalizedSearch)) {
                    return true;
                }
            }
        }
    }

    return false;
};

/**
 * Filters and searches entity data (drivers/units) based on entity type, option, and filters.
 * Returns [filteredData, totalCount]. For tags option, returns empty array (TagsManage handles its own data).
 */
const useEntityData = ({
    entity,
    option,
    includeInactiveDrivers,
    includeUnassignedDriver,
    searchText,
    drivers,
    units
}) => {
    return useMemo(() => {
        // TagsManage handles its own search and data, no processing needed
        if (option?.id === 'tags') {
            return [[], 0];
        }

        const normalizedSearch = typeof searchText === 'string'
            ? searchText.trim().toLowerCase()
            : '';
        const hasSearch = normalizedSearch.length > 0;

        const safeDrivers = ensureArray(drivers);
        const safeUnits = ensureArray(units);

        let baseData = [];

        if (entity?.id === 'Driver') {
            const driverPool = includeInactiveDrivers
                ? safeDrivers
                : safeDrivers.filter((driver) => driver?.status === 1);

            const sortedDrivers = sortAlpha(driverPool);
            baseData = includeUnassignedDriver
                ? [createUnassignedDriver(sortedDrivers[0]), ...sortedDrivers]
                : sortedDrivers;
        } else if (entity?.id === 'Unit') {
            baseData = sortAlpha(safeUnits);
        }

        const data = ensureArray(baseData);

        if (data.length === 0) {
            return [data, 0];
        }

        if (!hasSearch) {
            return [data, data.length];
        }

        const filtered = [];

        for (let index = 0; index < data.length; index += 1) {
            const item = data[index];

            if (matchesSearch(item, normalizedSearch)) {
                filtered.push(item);
            }
        }

        return [filtered, filtered.length];
    }, [
        entity,
        option,
        includeInactiveDrivers,
        includeUnassignedDriver,
        searchText,
        drivers,
        units
    ]);
};

export default useEntityData;